import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULT_EXECUTIVE_SUMMARY_GOALS,
  type ExecutiveSummaryGoals,
} from '../data/executiveSummaryData'
import {
  fetchExecutiveSummaryGoals,
  saveExecutiveSummaryGoals,
} from '../services/executiveSummaryGoalsService'

export function useExecutiveSummaryGoals() {
  const [goals, setGoals] = useState<ExecutiveSummaryGoals>(DEFAULT_EXECUTIVE_SUMMARY_GOALS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localOnlyWarning, setLocalOnlyWarning] = useState(false)
  const goalsRef = useRef(goals)

  useEffect(() => {
    goalsRef.current = goals
  }, [goals])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const loaded = await fetchExecutiveSummaryGoals()
        if (!cancelled) setGoals(loaded)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load executive summary goals')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const updateGoal = useCallback(async (key: keyof ExecutiveSummaryGoals, value: number) => {
    const previous = goalsRef.current
    const next = { ...previous, [key]: value }
    setGoals(next)
    setSaving(true)
    setError(null)
    setLocalOnlyWarning(false)

    try {
      const result = await saveExecutiveSummaryGoals(next)
      goalsRef.current = next
      if (!result.shared) {
        setLocalOnlyWarning(true)
      }
    } catch (err) {
      setGoals(previous)
      const message = err instanceof Error ? err.message : 'Failed to save goal'
      setError(message)
      throw new Error(message)
    } finally {
      setSaving(false)
    }
  }, [])

  return { goals, loading, saving, error, localOnlyWarning, setError, setLocalOnlyWarning, updateGoal }
}
